import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, Send, X } from 'lucide-react';
import { apiBase } from '../utils/authHeaders';
import { commandId } from './CkOperationsShared';
import { apiErrorMessage } from '../utils/apiError';
import { clearPendingWorkflow, loadPendingWorkflow, persistPendingWorkflow } from './operandiWorkflowQueue';

export default function OperandiHumanWorkflow(props) {
  const { runId, scope, headers } = props;
  const identity = JSON.stringify([runId, scope?.orgId, scope?.locationId, scope?.token, headers?.Authorization, headers?.['X-Location-Id']]);
  return <ScopedHumanWorkflow key={identity} {...props} />;
}

function ScopedHumanWorkflow({ runId, scope, headers, contexts = [], documents = [], onRecorded, onPendingChange }) {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [mode, setMode] = useState('question');
  const [selected, setSelected] = useState('');
  const [assignee, setAssignee] = useState('');
  const [deputy, setDeputy] = useState('');
  const [statement, setStatement] = useState('');
  const [evidence, setEvidence] = useState([]);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [recovering, setRecovering] = useState(true);
  const active = useRef(false);
  const loadRevision = useRef(0);
  const submitting = useRef(false);
  useEffect(() => { onPendingChange?.(!!pending || recovering); }, [pending, recovering, onPendingChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);
  const { orgId, locationId, token } = scope || {};
  useEffect(() => {
    let cancelled = false;
    setRecovering(true);
    loadPendingWorkflow({ orgId, locationId, token }, runId).then(saved => {
      if (cancelled) return;
      if (saved) {
        setPending(saved.request); setMode(saved.request.action); setStatement(saved.request.statement);
        setSelected(saved.request.thread_id || saved.request.target_context_id || '');
        setAssignee(saved.request.assigned_user_id || ''); setDeputy(saved.request.deputy_user_id || '');
        setEvidence(saved.request.evidence_refs.map(ref => ref.id));
        setMessage('Pending workflow recovered. Outcome not yet confirmed.');
      }
      setRecovering(false);
    });
    return () => { cancelled = true; };
  }, [orgId, locationId, token, runId]);
  const endpoint = `${apiBase()}/api/operandi/discovery-runs/${encodeURIComponent(runId)}/workflow`;
  const load = useCallback(async (cursor = null) => {
    const revision = ++loadRevision.current;
    setLoadingMore(!!cursor);
    setError('');
    try {
      const response = await fetch(`${endpoint}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`, { headers });
      const payload = await response.json();
      if (!response.ok) throw new Error(apiErrorMessage(payload.detail, 'Could not load questions.'));
      if (active.current && revision === loadRevision.current) {
        setItems(previous => cursor ? [...previous, ...(payload.items || []).filter(item => !previous.some(old => old.id === item.id))] : payload.items || []);
        setNextCursor(payload.next_cursor || null);
        if (payload.truncated && !cursor) setMessage('Showing the latest 100 participant threads.');
      }
    } catch (e) { if (active.current && revision === loadRevision.current) setError(e.message); }
    finally { if (active.current && revision === loadRevision.current) setLoadingMore(false); }
  }, [endpoint, headers]);
  useEffect(() => {
    active.current = true;
    load();
    (async () => {
      try {
        const response = await fetch(`${apiBase()}/api/operandi/workflow-recipients`, { headers });
        const payload = await response.json();
        if (!response.ok) throw new Error(apiErrorMessage(payload.detail, 'Could not load site respondents.'));
        if (active.current) setRecipients(payload.items || []);
      } catch (e) { if (active.current) setError(e.message); }
    })();
    return () => { active.current = false; loadRevision.current += 1; };
  }, [headers, load]);
  const submit = async event => {
    event.preventDefault();
    if (submitting.current || recovering) return;
    submitting.current = true;
    const chosen = items.find(item => item.id === selected);
    const request = pending || {
      command_id: commandId(), action: mode, statement: statement.trim(),
      evidence_refs: documents.filter(doc => evidence.includes(doc.id))
        .map(({ id, version, hash, kind }) => ({ id, version, hash, kind })),
      ...(mode === 'question' ? { assigned_user_id: assignee, deputy_user_id: deputy || null,
        target_context_id: selected || null } : mode === 'answer'
        ? { thread_id: selected, expected_version: chosen?.version }
        : { target_context_id: selected, expected_version: 1 }),
    };
    setPending(request); setBusy(true); setError(''); setMessage('');
    try {
      const saved = await persistPendingWorkflow(request, { orgId, locationId, token }, runId, undefined, () => active.current);
      if (!active.current) return;
      if (!saved) setMessage('Tab storage unavailable; keep this page open until the outcome is confirmed.');
      const response = await fetch(endpoint, { method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(request) });
      const payload = await response.json();
      if (!response.ok) throw new Error(apiErrorMessage(payload.detail, 'Workflow outcome unknown; retry the same command.'));
      if (payload.classification !== 'UNVERIFIED' || payload.no_action_authority !== true)
        throw new Error('Invalid acknowledgement; retry the same command.');
      if (!active.current) return;
      clearPendingWorkflow(request.command_id);
      setPending(null); setStatement(''); setEvidence([]);
      setMessage('Recorded as UNVERIFIED.'); onRecorded?.(); await load();
    } catch (e) { if (active.current) setError(e.message); }
    finally { submitting.current = false; if (active.current) setBusy(false); }
  };
  const targets = mode === 'answer' ? items.filter(item => item.can_answer) : contexts;
  const valid = statement.trim() && (mode === 'question' ? assignee && deputy !== assignee : selected);
  return <section className="mt-6 border-t border-gray-200 pt-5">
    <header className="flex items-center justify-between gap-3"><h2 className="font-semibold">Questions and disputes</h2>
      <button type="button" title="Refresh questions" aria-label="Refresh questions" onClick={() => load()} disabled={busy} className="min-h-11 min-w-11 rounded border border-gray-300"><RefreshCw size={18} className="mx-auto" /></button></header>
    {error && <p role="alert" className="mt-3 text-sm text-red-800">{error}</p>}
    {message && <p role="status" className="mt-3 text-sm text-green-800">{message}</p>}
    <div className="mt-4 grid gap-6 lg:grid-cols-2">
      <div>{items.length ? <>{items.map(item => <article key={item.id} className="border-b border-gray-200 py-3">
        <p className="text-xs font-medium text-gray-600">{item.interaction_kind} · {item.answer ? 'ANSWERED' : item.interaction_kind === 'DISPUTE' ? 'DISPUTED' : 'OPEN'} · UNVERIFIED</p>
        <p className="mt-1 break-words text-sm">{item.statement}</p>
        {item.answer && <p className="mt-2 break-words border-l-2 border-gray-300 pl-3 text-sm">{item.answer}</p>}
        {item.can_answer && <button type="button" disabled={!!pending || busy} className="mt-2 min-h-10 text-sm underline" onClick={() => { setMode('answer'); setSelected(item.id); }}>Answer question</button>}
      </article>)}{nextCursor && <button type="button" onClick={() => load(nextCursor)} disabled={loadingMore || busy} className="mt-3 min-h-11 rounded border border-gray-300 px-3 text-sm">{loadingMore ? 'Loading…' : 'Load more questions'}</button>}</> : <p className="text-sm text-gray-600">No questions or disputes assigned to you or raised by you.</p>}</div>
      <form onSubmit={submit} noValidate={!!pending}>
        <fieldset disabled={!!pending || busy || recovering}>
          <legend className="sr-only">Interaction type</legend>
          <div className="flex gap-4 text-sm">{['question', 'answer', 'dispute'].map(value => <label key={value} className="flex min-h-11 items-center gap-2"><input type="radio" name="workflow-mode" value={value} checked={mode === value} onChange={() => { setMode(value); setSelected(''); }} />{value[0].toUpperCase() + value.slice(1)}</label>)}</div>
          <label className="block text-sm font-medium" htmlFor="workflow-target">{mode === 'answer' ? 'Question' : 'Original observation'}</label>
          <select id="workflow-target" value={selected} required={mode !== 'question'} onChange={e => setSelected(e.target.value)} className="mt-1 min-h-11 w-full rounded border border-gray-300 px-2"><option value="">{mode === 'question' ? 'General process question' : 'Choose record'}</option>{targets.map(item => <option key={item.id} value={item.id}>{item.statement.slice(0, 90)}{item.disputed ? ' [DISPUTED]' : ''}</option>)}</select>
          {mode === 'question' && <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-sm">Respondent<select aria-label="Respondent" required value={assignee} onChange={e => setAssignee(e.target.value)} className="mt-1 min-h-11 w-full rounded border border-gray-300"><option value="">Choose person</option>{recipients.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></label>
            <label className="text-sm">Deputy<select aria-label="Deputy" value={deputy} onChange={e => setDeputy(e.target.value)} className="mt-1 min-h-11 w-full rounded border border-gray-300"><option value="">None</option>{recipients.filter(r => r.id !== assignee).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></label>
          </div>}
          <label htmlFor="workflow-statement" className="mt-3 block text-sm font-medium">{mode === 'question' ? 'Question text' : mode === 'answer' ? 'Answer text' : 'Dispute reason'}</label>
          <textarea id="workflow-statement" required maxLength={2000} value={statement} onChange={e => setStatement(e.target.value)} className="mt-1 min-h-28 w-full rounded border border-gray-300 p-3" />
          <fieldset className="mt-3"><legend className="text-sm font-medium">Evidence</legend>{documents.map(doc => <label key={doc.id} className="flex min-h-10 items-center gap-2 break-all text-sm"><input type="checkbox" checked={evidence.includes(doc.id)} disabled={!evidence.includes(doc.id) && evidence.length >= 10} onChange={e => setEvidence(ids => e.target.checked ? [...ids, doc.id] : ids.filter(id => id !== doc.id))} />{doc.filename}</label>)}</fieldset>
        </fieldset>
        <button type="submit" disabled={busy || recovering || (!pending && !valid)} className="mt-4 inline-flex min-h-11 items-center gap-2 rounded bg-gray-900 px-4 text-sm text-white disabled:opacity-50"><Send size={16} />{pending ? 'Retry same command' : 'Record'}</button>
        {pending && <button type="button" disabled={busy} onClick={() => { clearPendingWorkflow(pending.command_id); setPending(null); setError(''); setMessage('Retry discarded; this does not cancel a command already accepted by the server.'); }} className="ml-3 inline-flex min-h-11 items-center gap-1 text-sm"><X size={16} />Discard retry</button>}
      </form>
    </div>
  </section>;
}
